import { parseISO } from 'date-fns';

export const formatCurrency = (value) => {
  if (value === undefined || value === null || value === '') return '';
  const absValue = Math.abs(value);
  const formatted = absValue.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return value < 0 ? `(${formatted})` : `${formatted}`;
};


// Normalize any date-ish value to a plain calendar date — a Date pinned to
// local midnight, carrying only the year/month/day the value names. Returns
// null when the value is empty or unparseable. Accepts a Date, "M/D/YYYY" /
// "MM/DD/YYYY", or an ISO 8601 string.
//
// ISO strings are read LEXICALLY — the YYYY-MM-DD prefix is taken at face
// value and any time/offset is ignored. These values are calendar dates
// (posting dates, statement dates, dates of birth, filing dates), not
// instants, so converting them through the viewer's timezone is meaningless
// and shifts the day: "2024-06-15T00:00:00Z" read as an instant becomes
// 6/14/2024 for every user west of UTC. A forensic report must render the
// same date on every machine.
const ISO_YMD_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]|$)/;

export const parseDateValue = (dateInput) => {
  if (dateInput === undefined || dateInput === null || dateInput === '') return null;

  // Build a local-midnight Date, rejecting out-of-range parts (e.g. month 13)
  // by checking the constructed date didn't roll over into the next month.
  const calendarDate = (year, month, day) => {
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
    return date;
  };

  if (dateInput instanceof Date) {
    if (isNaN(dateInput.getTime())) return null;
    return new Date(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate());
  }
  if (typeof dateInput !== 'string') return null;

  const isoMatch = dateInput.match(ISO_YMD_RE);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return calendarDate(Number(year), Number(month), Number(day));
  }

  // Check for MM/DD/YYYY or M/D/YYYY format
  const slashMatch = dateInput.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, month, day, year] = slashMatch;
    return calendarDate(Number(year), Number(month), Number(day));
  }

  // Fall back to parseISO for the less common ISO shapes (e.g. "2024-W25-1")
  const date = parseISO(dateInput);
  if (isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

export const formatDate = (dateInput) => {
  const date = parseDateValue(dateInput);
  if (!date) return '';
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

// M/D/YYYY h:mm AM/PM in the viewer's local time. For the handful of places
// that record WHEN something happened rather than what calendar day it falls
// on — an export stamp, say — where two artifacts produced the same day have
// to be tellable apart. Note parseDateValue deliberately flattens to local
// midnight, so the time comes off the original Date, not off its result.
export const formatDateTime = (dateInput) => {
  const date = dateInput instanceof Date ? dateInput : parseDateValue(dateInput);
  if (!date || isNaN(date.getTime())) return '';

  const hours24 = date.getHours();
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const meridiem = hours24 < 12 ? 'AM' : 'PM';

  return `${formatDate(date)} ${hours12}:${minutes} ${meridiem}`;
};

// File size in the units a person expects: "812 bytes", "48 KB", "2.4 MB".
// Returns '' for anything that isn't a non-negative number.
export const formatFileSize = (bytes) => {
  const n = Number(bytes);
  if (bytes === null || bytes === undefined || bytes === '' || !Number.isFinite(n) || n < 0) return '';
  if (n < 1024) return `${n} ${n === 1 ? 'byte' : 'bytes'}`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${Math.round((n / (1024 * 1024)) * 10) / 10} MB`;
};

const SHORT_MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// Three-letter month abbreviation from a "YYYY-MM" bucket key, e.g.
// "2024-03" → "Mar". Takes the key directly rather than a Date so callers
// axis-labelling month buckets don't have to round-trip through a throwaway
// Date. Returns '' for anything that isn't a valid YYYY-MM.
export const formatMonthShort = (yearMonth) => {
  if (typeof yearMonth !== 'string') return '';
  const match = /^(\d{4})-(\d{2})/.exec(yearMonth);
  if (!match) return '';
  const monthIndex = Number(match[2]) - 1;
  if (monthIndex < 0 || monthIndex > 11) return '';
  return SHORT_MONTH_NAMES[monthIndex];
};

export const formatPercentage = (value) => {
  if (value === undefined || value === null || value === '') return '';
  return `${(value * 100).toFixed(1)}%`;
};

// Plain integer with thousands separators, e.g. 12530 → "12,530".
// For counts and quantities — not money (use formatCurrency for that).
export const formatInteger = (value) => {
  if (value === undefined || value === null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return Math.trunc(n).toLocaleString('en-US');
};

// Share / unit quantities: whole numbers render without decimals, fractional
// quantities keep 2–4 decimal places (fractional shares, DRIP lots). Unlike
// formatInteger this does NOT truncate the fraction. Returns '0' for empty and
// echoes the raw input back when it isn't numeric.
export const formatQuantity = (value) => {
  if (value === undefined || value === null || value === '') return '0';
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n % 1 === 0
    ? n.toLocaleString('en-US')
    : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

// Resolve the name to show for a single allocation entry / entity-shaped object
// ({ display_name, abbreviation, entity_id }). `abbreviate` opts in to the
// user's short form; it is OFF by default so that any caller which has not
// deliberately opted in renders the full legal name.
export function formatEntityName(entity, { abbreviate = false } = {}) {
  if (!entity) return '';
  const short = abbreviate ? (entity.abbreviation || '') : '';
  return short || entity.display_name || entity.entity_id || '';
}

// Render an account ownership allocation set (title[] / stated_source[]) as a
// compact string: "Jane Doe (50.0%), Acme LLC (50.0%)". Each item carries
// { display_name, abbreviation, pct } where pct is a 0-1 fraction. Returns ""
// for an empty / missing set so callers can fall back to the legacy free-text
// account_holder.
//
// Pass { abbreviate: true } to render each entity's user-defined short form
// where one is set ("J (50.0%)"). OPT-IN so a caller that hasn't been reviewed
// renders full legal names by default. On-screen views AND (per the 2026-07-21
// decision) the PDF/Excel exports opt in; every abbreviated export also carries
// an abbreviation→full-name legend (see collectAbbreviationLegend) so the short
// form stays traceable on a court-facing document that has no hover tooltip.
export function formatOwnershipAllocation(allocations, { abbreviate = false } = {}) {
  if (!Array.isArray(allocations) || allocations.length === 0) return '';
  return allocations
    .map((a) => {
      const name = formatEntityName(a, { abbreviate });
      const pct = formatPercentage(a?.pct);
      return name ? (pct ? `${name} (${pct})` : name) : '';
    })
    .filter(Boolean)
    .join(', ');
}

// Given allocation lists (title[] / source[] arrays, each item shaped like
// { display_name, abbreviation }), return the distinct abbreviation→full-name
// pairs actually used, sorted by abbreviation. Only entities that HAVE an
// abbreviation are included. Feeds the export legend so an abbreviated Title /
// Source on a court-facing PDF/Excel can be mapped back to the legal name.
// `allocationLists` is an array of arrays; nullish entries are skipped.
export function collectAbbreviationLegend(allocationLists) {
  const map = new Map();
  for (const list of allocationLists || []) {
    if (!Array.isArray(list)) continue;
    for (const a of list) {
      if (a?.abbreviation && a?.display_name && !map.has(a.abbreviation)) {
        map.set(a.abbreviation, a.display_name);
      }
    }
  }
  return [...map.entries()]
    .map(([abbreviation, display_name]) => ({ abbreviation, display_name }))
    .sort((x, y) => x.abbreviation.localeCompare(y.abbreviation));
}

// Walk an MBS/DRFA asset-liability data structure
// ({ assets: { category: { accounts: [...] } }, liabilities: {...} }) and return
// every ownership-title allocation list it contains — account titles plus each
// account's associated-liability titles. Used to build the export legend.
export function collectTitleAllocations(assetLiabilityData) {
  const lists = [];
  if (!assetLiabilityData) return lists;
  for (const group of ['assets', 'liabilities']) {
    const sections = assetLiabilityData[group] || {};
    for (const section of Object.values(sections)) {
      for (const account of (section?.accounts || [])) {
        if (Array.isArray(account?.title)) lists.push(account.title);
        for (const liab of (account?.associated_liabilities || [])) {
          if (Array.isArray(liab?.title)) lists.push(liab.title);
        }
      }
    }
  }
  return lists;
}

// Generic Title-Case humanizer for an account-type code, used ONLY as a
// client-side fallback when the API did not supply a resolved label. The
// canonical code→label mapping lives server-side in the doc-type schemas'
// `x-enum-labels` (resolved into `account_type_label` on the /account/summary
// response) — do NOT reintroduce a per-code map here. "MONEY_MARKET" ->
// "Money Market", "credit card" -> "Credit Card". Empty/non-string -> ''.
export function formatAccountType(type) {
  if (!type || typeof type !== 'string') return '';
  return type
    .trim()
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export function getClientName(caseDetails) {
  let clientName = caseDetails?.petitioner?.fullName;
  if (caseDetails?.respondent?.client) {
    clientName = caseDetails?.respondent?.fullName;
  }
  return clientName || 'Client Name';
}

export function getAge(birthDate) {
  if (!birthDate) return 'N/A';
  const date = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const m = today.getMonth() - date.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < date.getDate())) {
    age--;
  }
  return age.toString();
}

export function getClientAge(caseDetails) {
  let clientBirthDate = caseDetails?.petitioner?.birthDate;
  if (caseDetails?.respondent?.client) {
    clientBirthDate = caseDetails?.respondent?.birthDate;
  }
  return getAge(clientBirthDate);
}

export function getSpouseName(caseDetails) {
  let spouseName = caseDetails?.respondent?.fullName;
  if (caseDetails?.respondent?.client) {
    spouseName = caseDetails?.petitioner?.fullName;
  }
  return spouseName || 'Spouse Name';
}

export function getSpouseAge(caseDetails) {
  let spouseBirthDate = caseDetails?.respondent?.birthDate;
  if (caseDetails?.respondent?.client) {
    spouseBirthDate = caseDetails?.petitioner?.birthDate;
  }
  return getAge(spouseBirthDate);
}

export function getCustomerDisplayName(customer) {
  if (!customer) return '';
  if (customer.orgName) return customer.orgName;
  const first = customer.firstName || '';
  const last = customer.lastName || '';
  return `${first} ${last}`.trim() || customer.customerID;
}

export function getCustomerTypeLabel(customer) {
  if (!customer) return '';
  if (customer.customerType === 'organization' || customer.orgName) return 'Organization';
  return 'Individual';
}

// First-name + last-name initial, uppercased, for avatar circles.
// "Chase Belding" → "CB"; "Mary Jane Watson" → "MW"; single-word "Chase" → "C"
// (no last name to pair with); empty → fallback.
export function getInitials(name, fallback = 'U') {
  if (!name || typeof name !== 'string') return fallback;
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return fallback;
  if (words.length === 1) {
    return words[0][0].toUpperCase();
  }
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}